export interface CartAttribute {
    name: string;
    value: string;
}

export interface CartItem {
    idProduct: string;
    idProductVariant: string;
    name: string;
    price: number;
    quantity: number;
    image: string;
    stock?: number;
    attributes: CartAttribute[];
}

export interface AddCartItemInput extends Omit<CartItem, "quantity"> {
    quantity?: number;
}

export type CartOperation =
    | { type: "add"; item: AddCartItemInput }
    | { type: "remove"; idProduct: string; idProductVariant?: string }
    | {
          type: "updateQuantity";
          idProduct: string;
          idProductVariant?: string;
          quantity: number;
      }
    | { type: "clear" };

export type CartConcurrencyMode = "legacy" | "locked";

export interface CartStoreConfiguration {
    concurrencyMode: CartConcurrencyMode;
}

export interface CartPersistedState {
    items: CartItem[];
    revision: number;
    updatedAt: string;
}

export interface CartStorageEnvelope {
    state: CartPersistedState;
    version: 1;
}

export interface CartCommitResult {
    items: CartItem[];
    revision: number;
    changed: boolean;
}

export type CartPersistenceErrorCode =
    | "STORAGE_READ_FAILED"
    | "STORAGE_CORRUPTED"
    | "STORAGE_WRITE_FAILED"
    | "LOCK_UNAVAILABLE"
    | "LOCK_FAILED"
    | "UNSAFE_REPLACE_DISABLED";

export class CartPersistenceError extends Error {
    readonly code: CartPersistenceErrorCode;
    readonly cause?: unknown;

    constructor(code: CartPersistenceErrorCode, message: string, cause?: unknown) {
        super(message);
        this.name = "CartPersistenceError";
        this.code = code;
        this.cause = cause;
    }
}

export interface CartStore {
    items: CartItem[];
    persistedItems: CartItem[];
    revision: number;
    isOpen: boolean;
    isOverrideActive: boolean;

    /** @deprecated Usar beginCartOverride/endCartOverride antes de activar locked. */
    hydrateCartItems: (items: CartItem[]) => Promise<void>;
    beginCartOverride: (items: CartItem[]) => void;
    endCartOverride: () => void;
    applyOperations: (operations: CartOperation[]) => Promise<CartItem[]>;
    applyExternalSnapshot: (snapshot: CartPersistedState) => void;
    addToCart: (item: AddCartItemInput) => Promise<void>;
    removeFromCart: (idProduct: string, idProductVariant?: string) => Promise<void>;
    updateQuantity: (
        idProduct: string,
        idProductVariant: string | undefined,
        quantity: number,
    ) => Promise<void>;
    clearCart: () => Promise<void>;
    openCart: () => void;
    closeCart: () => void;
    toggleCart: () => void;
}
